import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <Button
            variant="outline"
            onClick={() => navigate('/login')}
            className="mb-4"
          >
            ← Voltar para Login
          </Button>
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Termos de Uso</h1>
            <p className="text-gray-600">Castle Green - Sistema de Gestão de Projetos Ambientais</p>
            <p className="text-sm text-gray-500 mt-2">Última atualização: {new Date().toLocaleDateString('pt-BR')}</p>
          </div>
        </div>

        <Card className="p-8">
          <div className="prose prose-gray max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Aceitação dos Termos</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Ao acessar e usar o Castle Green ("Sistema", "Plataforma", "Serviço"), você concorda em cumprir e estar vinculado a estes Termos de Uso. Se você não concordar com qualquer parte destes termos, não utilize o Sistema.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Descrição do Serviço</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                O Castle Green é uma plataforma de gestão de projetos de consultoria ambiental que oferece:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>Gestão e acompanhamento de projetos ambientais</li>
                <li>Controle de fases e tarefas de projetos</li>
                <li>Gestão de licenças ambientais e validades</li>
                <li>Calendário de renovações e alertas automáticos</li>
                <li>Dashboard com métricas e indicadores</li>
                <li>Sistema de notificações e alertas</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Cadastro e Acesso</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>3.1 Autorização de Acesso:</strong> O acesso ao Sistema é restrito a usuários previamente autorizados pela empresa administradora. A tentativa de acesso não autorizado pode resultar em ações legais.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>3.2 Autenticação:</strong> O Sistema utiliza autenticação via Google OAuth. Você é responsável por manter a segurança de sua conta Google.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>3.3 Perfis de Usuário:</strong> Existem três tipos de perfis:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
                <li><strong>Administrador:</strong> Acesso total ao sistema, gestão de usuários</li>
                <li><strong>Consultor:</strong> Gestão de projetos, fases, tarefas e licenças</li>
                <li><strong>Cliente:</strong> Visualização de projetos próprios (somente leitura)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Uso Aceitável</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Você concorda em usar o Sistema apenas para fins legítimos e de acordo com estes Termos. É proibido:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>Violar qualquer lei ou regulamento aplicável</li>
                <li>Infringir direitos de propriedade intelectual</li>
                <li>Transmitir vírus, malware ou código malicioso</li>
                <li>Tentar acesso não autorizado a sistemas ou dados</li>
                <li>Interferir com o funcionamento do Sistema</li>
                <li>Usar o Sistema para fins comerciais não autorizados</li>
                <li>Compartilhar credenciais de acesso com terceiros</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Propriedade Intelectual</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>5.1 Propriedade do Sistema:</strong> O Castle Green, incluindo todo seu código-fonte, design, funcionalidades e documentação, é propriedade exclusiva de seus desenvolvedores e está protegido por leis de direitos autorais.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>5.2 Dados do Usuário:</strong> Você mantém todos os direitos sobre os dados que insere no Sistema. Ao usar o Sistema, você nos concede uma licença limitada para processar, armazenar e exibir seus dados conforme necessário para fornecer o Serviço.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Privacidade e Proteção de Dados</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>6.1 LGPD:</strong> Processamos dados pessoais em conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018).
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>6.2 Dados Coletados:</strong> Coletamos apenas os dados necessários para o funcionamento do Sistema:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
                <li>Nome e email (via Google OAuth)</li>
                <li>Dados de projetos, fases e tarefas inseridos por você</li>
                <li>Informações de licenças ambientais</li>
                <li>Logs de acesso para segurança</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>6.3 Uso de Dados:</strong> Seus dados são utilizados exclusivamente para:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
                <li>Prover funcionalidades do Sistema</li>
                <li>Enviar alertas e notificações relevantes</li>
                <li>Melhorar a segurança e desempenho</li>
                <li>Cumprir obrigações legais</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>6.4 Compartilhamento:</strong> Não compartilhamos seus dados com terceiros, exceto:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
                <li>Quando exigido por lei</li>
                <li>Com seu consentimento explícito</li>
                <li>Com provedores de serviços essenciais (hospedagem, autenticação)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Disponibilidade do Serviço</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>7.1 Disponibilidade:</strong> Nos esforçamos para manter o Sistema disponível 24/7, mas não garantimos acesso ininterrupto.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>7.2 Manutenção:</strong> Podemos realizar manutenções programadas, notificando com antecedência quando possível.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>7.3 Backup:</strong> Realizamos backups regulares, mas recomendamos que você mantenha cópias importantes de seus dados.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Limitação de Responsabilidade</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>8.1 Uso por Sua Conta e Risco:</strong> O Sistema é fornecido "como está". Você usa o Sistema por sua própria conta e risco.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>8.2 Sem Garantias:</strong> Não garantimos que o Sistema será livre de erros, seguro ou sempre disponível.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>8.3 Conformidade Regulatória:</strong> O Sistema fornece ferramentas para gestão de licenças, mas a responsabilidade pela conformidade regulatória é exclusivamente sua.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>8.4 Limitação de Danos:</strong> Em nenhuma circunstância seremos responsáveis por danos indiretos, incidentais ou consequenciais.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Modificações do Serviço e dos Termos</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>9.1 Atualizações:</strong> Podemos modificar, suspender ou descontinuar qualquer aspecto do Sistema a qualquer momento.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>9.2 Alteração de Termos:</strong> Reservamos o direito de alterar estes Termos a qualquer momento. Alterações significativas serão notificadas por email ou através do Sistema.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>9.3 Continuação de Uso:</strong> O uso continuado após alterações dos Termos constitui aceitação das novas condições.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Rescisão</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>10.1 Por Você:</strong> Você pode encerrar o uso do Sistema a qualquer momento, solicitando a desativação de sua conta.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>10.2 Por Nós:</strong> Podemos suspender ou encerrar seu acesso imediatamente, sem aviso prévio, se:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
                <li>Você violar estes Termos</li>
                <li>Seu uso representar risco de segurança</li>
                <li>Houver suspeita de atividade fraudulenta</li>
                <li>Por determinação legal</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>10.3 Efeitos da Rescisão:</strong> Após o encerramento, você perderá acesso aos dados no Sistema. Podemos reter dados conforme exigido por lei.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Lei Aplicável e Foro</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>11.1 Lei Brasileira:</strong> Estes Termos são regidos pelas leis da República Federativa do Brasil.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>11.2 Foro:</strong> Fica eleito o foro da Comarca de [Sua Cidade], com exclusão de qualquer outro, por mais privilegiado que seja, para dirimir questões oriundas destes Termos.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Disposições Gerais</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>12.1 Acordo Integral:</strong> Estes Termos constituem o acordo integral entre você e o Castle Green.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>12.2 Severabilidade:</strong> Se qualquer disposição for considerada inválida, as demais permanecem em vigor.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>12.3 Renúncia:</strong> A não aplicação de qualquer disposição não constitui renúncia de direitos.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Contato</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Para questões sobre estes Termos de Uso, privacidade ou funcionamento do Sistema, entre em contato através dos canais oficiais do Castle Green.
              </p>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mt-4">
                <p className="text-sm text-emerald-800">
                  <strong>Importante:</strong> Ao fazer login no Castle Green, você declara ter lido, compreendido e concordado com todos os termos aqui estabelecidos.
                </p>
              </div>
            </section>
          </div>
        </Card>

        <div className="mt-8 text-center">
          <Button
            onClick={() => navigate('/login')}
            className="bg-emerald-800 hover:bg-emerald-900"
          >
            Voltar para Login
          </Button>
        </div>
      </div>
    </div>
  );
}

export default TermsOfService;